import { useEffect, useState, useCallback, useMemo } from 'react';
import api from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Badge } from '@/components/ui/badge';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { ShieldCheck, Lock, Eye, PencilSimple, Trash, FloppyDisk, ArrowCounterClockwise } from '@phosphor-icons/react';
import { toast } from 'sonner';
import { PAGES } from '@/lib/permissions';
import { useAuth } from '@/context/AuthContext';

/**
 * Staff Permissions matrix.
 * Columns = staff users (zebra-striped pairs of V/E/D triplets), Rows = page heads
 * (zebra-striped). Edits are buffered locally and persisted via the explicit
 * "Save Changes" button. The "Discard" button reverts to the server state.
 *
 * Auto-elevation rules (mirror the backend):
 *  - Enabling Edit or Delete forces View ON.
 *  - Disabling View cascades to OFF for Edit and Delete.
 */
export default function StaffPermissionsCard() {
  const { user, refreshPermissions } = useAuth();
  // The "server" snapshot we last fetched / saved successfully.
  const [serverStaff, setServerStaff] = useState([]);
  // The local working copy the user is editing.
  const [draftStaff, setDraftStaff] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const fetch = useCallback(async () => {
    try {
      const { data } = await api.get('/staff/permissions');
      // Deep-clone for the draft so user mutations don't touch the server snapshot.
      setServerStaff(data);
      setDraftStaff(JSON.parse(JSON.stringify(data)));
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to load permissions');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetch(); }, [fetch]);

  // List of staff/page combos that have changed vs. the server snapshot.
  const dirtyKeys = useMemo(() => {
    const out = new Set();
    draftStaff.forEach(d => {
      const original = serverStaff.find(s => s.user_id === d.user_id);
      if (!original) return;
      Object.keys(d.permissions).forEach(pageKey => {
        const a = d.permissions[pageKey];
        const b = original.permissions[pageKey] || { view: false, edit: false, delete: false };
        if (a.view !== b.view || a.edit !== b.edit || a.delete !== b.delete) {
          out.add(`${d.user_id}:${pageKey}`);
        }
      });
    });
    return out;
  }, [draftStaff, serverStaff]);

  if (user?.role !== 'admin') return null;

  const togglePerm = (staffMember, pageKey, action, nextValue) => {
    setDraftStaff(prev => prev.map(s => {
      if (s.user_id !== staffMember.user_id) return s;
      const next = JSON.parse(JSON.stringify(s.permissions));
      next[pageKey] = next[pageKey] || { view: false, edit: false, delete: false };
      next[pageKey][action] = nextValue;
      // Auto-elevation: Edit/Delete imply View; turning View off cascades the rest.
      if (nextValue && (action === 'edit' || action === 'delete')) {
        next[pageKey].view = true;
      }
      if (!nextValue && action === 'view') {
        next[pageKey].edit = false;
        next[pageKey].delete = false;
      }
      return { ...s, permissions: next };
    }));
  };

  const handleSave = async () => {
    if (dirtyKeys.size === 0) return;
    setSaving(true);
    // Determine which staff members have any change and PUT only those.
    const dirtyStaffIds = new Set([...dirtyKeys].map(k => k.split(':')[0]));
    let success = 0, failed = 0;
    for (const staffId of dirtyStaffIds) {
      const draft = draftStaff.find(d => d.user_id === staffId);
      if (!draft) continue;
      try {
        await api.put(`/staff/permissions/${staffId}`, { permissions: draft.permissions });
        success += 1;
      } catch (err) {
        failed += 1;
        toast.error(`Failed for ${draft.name}: ${err.response?.data?.detail || 'error'}`);
      }
    }
    if (success > 0) toast.success(`Saved permissions for ${success} staff${failed ? ` (${failed} failed)` : ''}`);
    refreshPermissions?.();
    fetch(); // Re-sync server snapshot.
    setSaving(false);
  };

  const handleDiscard = () => {
    setDraftStaff(JSON.parse(JSON.stringify(serverStaff)));
    toast('Changes discarded', { icon: '\u21BA' });
  };

  if (loading) {
    return (
      <Card className="rounded-xl border-[#E4E4E7] shadow-sm">
        <CardContent className="p-6 text-sm text-[#52525B]">Loading staff permissions...</CardContent>
      </Card>
    );
  }

  // Zebra column tint per staff (alternating). Each staff occupies 3 cells (V/E/D),
  // so we tint odd-index staff blocks with a soft slate background.
  const colTint = (idx) => idx % 2 === 1 ? 'bg-[#F8FAFC]' : 'bg-white';
  const colHeaderTint = (idx) => idx % 2 === 1 ? 'bg-[#F1F5F9]' : 'bg-[#FAFAFA]';

  return (
    <TooltipProvider delayDuration={150}>
      <Card className="rounded-xl border-[#E4E4E7] shadow-sm" data-testid="staff-permissions-card">
        <CardHeader className="p-5 pb-2">
          <div className="flex items-start justify-between gap-3 flex-wrap">
            <div>
              <CardTitle className="text-base font-semibold flex items-center gap-2">
                <ShieldCheck size={18} className="text-[#10B981]" weight="duotone" /> Staff Permissions
              </CardTitle>
              <p className="text-xs text-[#52525B] mt-0.5">
                Toggle View / Edit / Delete rights per staff per page. Click <span className="font-semibold text-[#10B981]">Save Changes</span> to apply.
                <span className="text-[#A1A1AA]"> Edit or Delete automatically enables View.</span>
              </p>
            </div>
            <div className="flex items-center gap-1 text-[10px] text-[#A1A1AA]">
              <Lock size={11} /> Staffs &amp; Settings are admin-only.
            </div>
          </div>
        </CardHeader>
        <CardContent className="p-5 pt-3">
          {draftStaff.length === 0 ? (
            <p className="text-sm text-[#A1A1AA] italic text-center py-6">No staff users yet. Create staff from the Staffs page first.</p>
          ) : (
            <>
              <div className="overflow-x-auto rounded-lg border border-[#E4E4E7]" data-testid="permissions-matrix-scroll">
                <table className="min-w-full border-separate border-spacing-0">
                  <thead>
                    <tr>
                      <th className="sticky left-0 z-10 bg-white text-left text-[10px] uppercase tracking-[0.12em] font-bold text-[#52525B] px-3 py-2 border-b border-[#E4E4E7] min-w-[140px]">
                        Page Head
                      </th>
                      {draftStaff.map((s, idx) => (
                        <th
                          key={s.user_id}
                          colSpan={3}
                          className={`text-center text-[10px] uppercase tracking-[0.12em] font-bold text-[#0A0A0A] px-2 py-2 border-b border-[#E4E4E7] border-l border-[#E4E4E7] ${colHeaderTint(idx)}`}
                          data-testid={`staff-col-${s.user_id}`}
                        >
                          <div className="flex flex-col items-center gap-0.5">
                            <span className="truncate max-w-[150px]">{s.name}</span>
                            {!s.active && <Badge variant="outline" className="rounded-md text-[8px] bg-[#FEE2E2] text-[#DC2626] border-[#FECACA]">inactive</Badge>}
                          </div>
                        </th>
                      ))}
                    </tr>
                    <tr>
                      <th className="sticky left-0 z-10 bg-white px-2 py-1.5 border-b border-[#E4E4E7]" />
                      {draftStaff.map((s, idx) => (
                        <ActionHeaders key={s.user_id} tint={colHeaderTint(idx)} />
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {PAGES.map((p, rowIdx) => {
                      // Row zebra: alternate page rows softly.
                      const rowZebra = rowIdx % 2 === 1 ? 'bg-[#FAFAFA]' : 'bg-white';
                      return (
                        <tr key={p.key} className="hover:bg-[#F1F5F9]/60" data-testid={`perm-row-${p.key}`}>
                          <td className={`sticky left-0 z-10 ${rowZebra} text-xs font-medium text-[#0A0A0A] px-3 py-2 border-b border-[#F4F4F5]`}>
                            {p.label}
                          </td>
                          {draftStaff.map((s, colIdx) => {
                            const cellKey = `${s.user_id}:${p.key}`;
                            const cell = s.permissions[p.key] || { view: false, edit: false, delete: false };
                            const isDirty = dirtyKeys.has(cellKey);
                            return (
                              <PermCells
                                key={s.user_id}
                                page={p}
                                staff={s}
                                cell={cell}
                                isDirty={isDirty}
                                onToggle={togglePerm}
                                tint={colTint(colIdx)}
                              />
                            );
                          })}
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>

              <div className="flex items-center justify-between mt-4 gap-3 flex-wrap">
                <p className="text-[10px] text-[#A1A1AA] italic">
                  <span className="font-mono">—</span> means the action does not apply to that page (e.g. Reports has no Edit/Delete).
                </p>
                <div className="flex items-center gap-2">
                  {dirtyKeys.size > 0 && (
                    <Badge variant="outline" className="rounded-md text-[10px] bg-[#FEF3C7] text-[#92400E] border-[#FDE68A]" data-testid="dirty-badge">
                      {dirtyKeys.size} unsaved change{dirtyKeys.size === 1 ? '' : 's'}
                    </Badge>
                  )}
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={handleDiscard}
                    disabled={saving || dirtyKeys.size === 0}
                    className="rounded-lg border-[#E4E4E7] text-[#52525B] hover:bg-[#F4F4F5]"
                    data-testid="discard-perms-btn"
                  >
                    <ArrowCounterClockwise size={12} className="mr-1" /> Discard
                  </Button>
                  <Button
                    size="sm"
                    onClick={handleSave}
                    disabled={saving || dirtyKeys.size === 0}
                    className="rounded-lg bg-[#10B981] text-white hover:bg-[#059669] hover:text-white"
                    data-testid="save-perms-btn"
                  >
                    <FloppyDisk size={12} className="mr-1" weight="bold" />
                    {saving ? 'Saving...' : 'Save Changes'}
                  </Button>
                </div>
              </div>
            </>
          )}
        </CardContent>
      </Card>
    </TooltipProvider>
  );
}

function ActionHeaders({ tint }) {
  return (
    <>
      <th className={`text-[9px] uppercase tracking-[0.12em] font-semibold text-[#3B82F6] px-2 py-1.5 border-b border-[#E4E4E7] border-l border-[#E4E4E7] w-[44px] text-center ${tint}`}>
        <Eye size={12} className="inline mr-0.5" /> V
      </th>
      <th className={`text-[9px] uppercase tracking-[0.12em] font-semibold text-[#F59E0B] px-2 py-1.5 border-b border-[#E4E4E7] w-[44px] text-center ${tint}`}>
        <PencilSimple size={12} className="inline mr-0.5" /> E
      </th>
      <th className={`text-[9px] uppercase tracking-[0.12em] font-semibold text-[#EF4444] px-2 py-1.5 border-b border-[#E4E4E7] w-[44px] text-center ${tint}`}>
        <Trash size={12} className="inline mr-0.5" /> D
      </th>
    </>
  );
}

function PermCells({ page, staff, cell, isDirty, onToggle, tint }) {
  const Cell = ({ action, available, leftBorder }) => {
    if (!available) {
      return (
        <td className={`text-center px-2 py-2 border-b border-[#F4F4F5] text-[#D4D4D8] text-xs ${tint} ${leftBorder ? 'border-l border-[#E4E4E7]' : ''} ${isDirty ? 'ring-1 ring-inset ring-[#F59E0B]/40' : ''}`}>
          <Tooltip>
            <TooltipTrigger><span className="font-mono">—</span></TooltipTrigger>
            <TooltipContent side="top" className="bg-[#0A0A0A] text-white border-0 text-[10px] rounded-md">
              {page.label} has no {action} action
            </TooltipContent>
          </Tooltip>
        </td>
      );
    }
    return (
      <td className={`text-center px-2 py-2 border-b border-[#F4F4F5] ${tint} ${leftBorder ? 'border-l border-[#E4E4E7]' : ''} ${isDirty ? 'ring-1 ring-inset ring-[#F59E0B]/40' : ''}`}>
        <Checkbox
          checked={!!cell[action]}
          onCheckedChange={(v) => onToggle(staff, page.key, action, !!v)}
          className="data-[state=checked]:bg-[#10B981] data-[state=checked]:border-[#10B981]"
          data-testid={`perm-${staff.user_id}-${page.key}-${action}`}
        />
      </td>
    );
  };
  return (
    <>
      <Cell action="view" available={true} leftBorder />
      <Cell action="edit" available={page.hasEdit} />
      <Cell action="delete" available={page.hasDelete} />
    </>
  );
}
